import { useCallback, useEffect, useMemo, useRef } from "react";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";

import { useIsJFindInviteStepExperiment } from "../../../../hooks/experiment-hooks";

import {
    FetchParams,
    Jfind1663FetchInterceptorParams,
    InterceptPaths
} from "./types";
import { useJfind1663TaskPolling } from "./use-jfind-1663-create-project-task-polling-context";
import { getInviteeProjectRoleId, requestHandlers } from "./utils";

export const useJfind1663FetchInterceptor = ({
    cloudId
}: Jfind1663FetchInterceptorParams) => {
    const originalFetch = useRef(global?.fetch); // DO NOT MODIFY
    const fetchRef = originalFetch.current;

    const isEnrolled = useIsJFindInviteStepExperiment();
    const { projectId, projectRoleIds, jiraEdition } =
        useJfind1663TaskPolling();
    const { createAnalyticsEvent } = useAnalyticsEvents();

    const preferAdminRole = jiraEdition === "free"; // Free edition requires users to be invited as Admins
    const inviteeRoleId = useMemo(
        () => getInviteeProjectRoleId(projectRoleIds, preferAdminRole),
        [preferAdminRole, projectRoleIds]
    );

    const getJfind1663Interceptor = useCallback(
        ({ cloudId }: Jfind1663FetchInterceptorParams) =>
            async (...fetched: FetchParams) => {
                const [resource] = fetched;
                const fetchedString = resource.toString();
                const isIntercepted = InterceptPaths.some((path) =>
                    fetchedString.includes(path)
                );
                const isRelativePath = !fetchedString.startsWith("http");

                if (isEnrolled && isIntercepted && isRelativePath) {
                    const targetPath = InterceptPaths.find((path) =>
                        fetchedString.includes(path)
                    );

                    if (targetPath !== undefined && fetchRef) {
                        const handler = requestHandlers[targetPath];

                        try {
                            const onComplete = async (args: object) => {
                                await Promise.resolve(
                                    createAnalyticsEvent({
                                        action: "succeeded",
                                        actionSubject:
                                            "jfind1663FetchInterceptor",
                                        eventType: "track",
                                        attributes: {
                                            ...args,
                                            path: targetPath,
                                            projectId,
                                            inviteeRoleId,
                                            jiraEdition
                                        }
                                    }).fire()
                                );
                            };

                            const onError = async (args: object) => {
                                createAnalyticsEvent({
                                    action: "failed",
                                    actionSubject:
                                        "onboardingWizardInviteStepInviteRequest",
                                    eventType: "operational",
                                    attributes: {
                                        ...args,
                                        path: targetPath,
                                        projectId,
                                        inviteeRoleId,
                                        jiraEdition
                                    }
                                }).fire();
                            };

                            return handler(fetchRef, fetched, {
                                cloudId,
                                projectId,
                                inviteeRoleId,
                                onComplete,
                                onError
                            });
                        } catch (error) {
                            // fail silently and fire analytics
                            createAnalyticsEvent({
                                action: "failed",
                                actionSubject: "jfind1663FetchInterceptor",
                                eventType: "track",
                                attributes: {
                                    error: (error as Error)?.message,
                                    path: targetPath,
                                    projectId,
                                    inviteeRoleId,
                                    jiraEdition
                                }
                            }).fire();
                        }
                    }
                }

                // call original fetch as a default
                return fetchRef?.(...fetched);
            },
        [createAnalyticsEvent, fetchRef, inviteeRoleId, isEnrolled, projectId]
    );

    // Set the interceptor
    useEffect(() => {
        if (!fetchRef) {
            // If in a non-browser environment, do nothing
            return;
        }

        Object.assign(global, {
            fetch: getJfind1663Interceptor({ cloudId })
        });

        return () => {
            // Restore original fetch on unmount
            Object.assign(global, {
                fetch: fetchRef
            });
        };
    }, [cloudId, fetchRef, getJfind1663Interceptor]);
};
